import { Company, LiveAttributionStats, LiveAttributionWindow, Order, ProcessProgress, SegmentProcessResult, StoryConversion } from '../types'
import { RoiData } from '../hooks/useRoi'
import { CompanyStatsCard } from './CompanyStatsCard'
import { SegmentStatsCard } from './SegmentStatsCard'
import { LiveStatsCard } from './LiveStatsCard'
import { OrdersTable } from './OrdersTable'
import { ProcessButton, ProcessStatus } from './ProcessButton'
import { ProductDiagnosticsCard } from './ProductDiagnosticsCard'

interface OrderStats {
  totalRevenue: number
  widdeCount: number
  widdeRevenue: number
  influencedRevenue: number
  directCount: number
}

export interface DashboardPageProps {
  productMode: 'stories' | 'live'
  selectionMode: 'company' | 'group' | null
  selectedCompany: Company | null
  startDate: string
  endDate: string
  forceCache: boolean
  setForceCache: (v: boolean) => void
  liveAttributionWindow: LiveAttributionWindow
  liveStats: LiveAttributionStats | null
  storyConversions: StoryConversion[] | null
  /** Produtos e audiência da última execução, com o ESTADO que explica o vazio. */
  productDiag: {
    byProduct: any[] | null
    topViewed: StoryConversion[] | null
    estado: any
    motivo: string | null
    snapshotsAvailable: boolean | null
  } | null
  stats: OrderStats | null
  allOrders: Order[]
  orders: Order[]
  roiData: RoiData | null
  roiLoading: boolean
  datesChanged: boolean
  segmentResult: SegmentProcessResult | null
  progress: ProcessProgress | null
  isConnected: boolean
  isStarting: boolean
  isFormValid: boolean
  showToast: (msg: string) => void
  onStartProcess: (options: any) => Promise<{ sessionId: string }>
  onSessionStart: (sessionId: string) => void
  /** Presente só enquanto um processamento roda. */
  onCancelProcess?: () => Promise<void>
  /**
   * O resultado visível foi calculado com filtros diferentes dos atuais:
   * `shown` = filtros dele; `changed` = o que mudou ("o período e a janela...").
   */
  staleResult: { shown: string; changed: string } | null
  /** Período com que o resultado visível foi calculado (pode não ser o do filtro). */
  resultPeriod: { startDate: string; endDate: string } | null
  setSelectedOrder: (order: any) => void
}

/**
 * Resultado de filtros antigos: aviso explícito em cima e números esmaecidos.
 * Trocar período/janela não reprocessa sozinho (cada execução leva minutos),
 * então a tela tem que dizer que o que está nela ainda é o de antes.
 */
function StaleResults({ stale, processLabel, children }: {
  stale: { shown: string; changed: string } | null
  processLabel: string
  children: React.ReactNode
}) {
  if (!stale) return <>{children}</>
  return (
    <>
      <div
        role="status"
        style={{
          position: 'sticky', top: 0, zIndex: 5,
          margin: '20px 0 0', padding: '12px 16px', borderRadius: 8,
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
          fontSize: 13, lineHeight: 1.5, display: 'flex', gap: 10, alignItems: 'flex-start',
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1.2 }}>⚠</span>
        <span>
          <strong>Resultado desatualizado.</strong> Você mudou {stale.changed}, mas os números abaixo ainda são
          do processamento anterior (<strong>{stale.shown}</strong>). Clique em <strong>{processLabel}</strong> para
          calcular com os filtros atuais.
        </span>
      </div>
      <div aria-hidden={false} style={{ opacity: 0.45, filter: 'grayscale(0.6)', transition: 'opacity .2s' }}>
        {children}
      </div>
    </>
  )
}

// Product section divider — clearly identifies which product's data follows
function ProductSectionLabel({ product }: { product: 'stories' | 'live' }) {
  const isVC = product === 'stories'
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      margin: '28px 0 14px',
      paddingBottom: 10,
      borderBottom: `2px solid ${isVC ? 'var(--brand-200)' : '#fbd5c5'}`,
    }}>
      {isVC ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand-600)', flexShrink: 0 }}><polygon points="5 3 19 12 5 21 5 3"/></svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#c2410c', flexShrink: 0 }}><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14"/></svg>
      )}
      <span style={{ fontWeight: 700, fontSize: 13, color: isVC ? 'var(--brand-700)' : '#c2410c', letterSpacing: '-0.01em' }}>
        {isVC ? 'Video Commerce' : 'Live Commerce'}
      </span>
      <span style={{ fontSize: 12, color: 'var(--ink-400)' }}>
        {isVC ? 'atribuição por stories e vídeos' : 'atribuição por transmissões ao vivo'}
      </span>
    </div>
  )
}

// Dashboard page — extracted from AppContent
export function DashboardPage({
  productMode,
  selectionMode,
  selectedCompany,
  startDate,
  endDate,
  forceCache,
  setForceCache,
  liveAttributionWindow,
  liveStats,
  storyConversions,
  productDiag,
  stats,
  allOrders,
  orders,
  roiData,
  roiLoading,
  datesChanged,
  segmentResult,
  progress,
  isConnected,
  isStarting,
  isFormValid,
  showToast,
  onStartProcess,
  onSessionStart,
  onCancelProcess,
  staleResult,
  resultPeriod,
  setSelectedOrder
}: DashboardPageProps) {
  // Os cards rotulam e exportam o período DO RESULTADO, não o do filtro.
  const resultStart = resultPeriod?.startDate ?? startDate
  const resultEnd = resultPeriod?.endDate ?? endDate
  const productLabel = productMode === 'stories' ? 'Video Commerce' : 'Live Commerce'
  const contextParts = [
    selectedCompany ? selectedCompany.title : (selectionMode === 'group' ? 'Grupo filtrado' : 'Todas as empresas'),
    startDate && endDate ? `${startDate.slice(0, 7)} a ${endDate.slice(0, 7)}` : null,
    productLabel,
  ].filter(Boolean)

  return (
    <>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: 'var(--ink-400)', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700, marginBottom: 4 }}>Dashboard</div>
        <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)', display: 'flex', alignItems: 'center', gap: 10 }}>
          Visão Geral
          <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 999, background: productMode === 'stories' ? 'var(--brand-100)' : 'var(--accent-coral-subtle, #fff4f0)', color: productMode === 'stories' ? 'var(--brand-700)' : '#c2410c', border: `1px solid ${productMode === 'stories' ? 'var(--brand-200)' : '#fbd5c5'}` }}>
            {productLabel}
          </span>
        </h1>
        <div style={{ fontSize: 13, color: 'var(--ink-500)' }}>{contextParts.join(' · ')}</div>
      </div>

      {/* Process form card */}
      <div className="card">
        <h2>Processar Análise Completa</h2>

        {/* Live empty state warning */}
        {productMode === 'live' && selectionMode !== 'company' && (
          <div style={{
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 13,
            color: '#92400e',
          }}>
            <span style={{ fontSize: 16 }}>⚠</span>
            <span>Selecione uma empresa para continuar — Live Commerce não suporta processamento por grupo.</span>
          </div>
        )}

        {/* Process buttons — conditional on productMode */}
        {productMode === 'stories' ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <ProcessButton
              onStartProcess={onStartProcess}
              onSessionStart={onSessionStart}
              progress={progress}
              isConnected={isConnected}
              disabled={!isFormValid}
              companyKey={selectedCompany?.key || ''}
              startDate={startDate}
              endDate={endDate}
              forceDownload={forceCache}
              forceProcess={forceCache}
              mode="stories"
              onCancel={onCancelProcess}
            />
            <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
              janela 30d (alinhada com a audience GA4)
            </span>
          </div>
        ) : (
          selectionMode === 'company' && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <ProcessButton
                  onStartProcess={onStartProcess}
                  onSessionStart={onSessionStart}
                  progress={progress}
                  isConnected={isConnected}
                  disabled={!isFormValid}
                  companyKey={selectedCompany?.key || ''}
                  startDate={startDate}
                  endDate={endDate}
                  forceDownload={forceCache}
                  forceProcess={forceCache}
                  mode="live"
                  liveAttributionWindow={liveAttributionWindow}
                  variant="live"
                  onCancel={onCancelProcess}
                />
                <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                  janela <strong>{liveAttributionWindow}</strong>
                </span>
              </div>
            </div>
          )
        )}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--ink-500)', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={forceCache}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForceCache(e.target.checked)}
              style={{ width: 'auto', margin: 0 }}
            />
            Ignorar cache
          </label>
        </div>
        {/* O "Cancelar" fica ao lado do botão de processar. */}
        <ProcessStatus
          progress={progress}
          isConnected={isConnected}
          isStarting={isStarting}
        />
      </div>

      {/* ── Video Commerce data section ──────────────────────── */}
      {productMode === 'stories' && selectionMode === 'company' && (
        <StaleResults stale={staleResult} processLabel="Processar Stories">
          {stats && (
            <>
              <ProductSectionLabel product="stories" />
              <CompanyStatsCard
                stats={stats}
                storyConversions={storyConversions}
                allOrders={allOrders}
                selectedCompany={selectedCompany}
                startDate={resultStart}
                endDate={resultEnd}
                roiData={roiData}
                roiLoading={roiLoading}
                datesChanged={datesChanged}
                showToast={showToast}
              />
            </>
          )}
          {/* Fora do `stats &&`: quando a execucao nao produz produto nenhum, e
              JUSTAMENTE o estado que precisa aparecer. Esconder o card no vazio
              devolveria a ambiguidade que ele existe para eliminar. */}
          {productDiag && (
            <ProductDiagnosticsCard
              byProduct={productDiag.byProduct}
              topViewed={productDiag.topViewed}
              topViewedEstado={productDiag.estado}
              topViewedMotivo={productDiag.motivo}
              snapshotsAvailable={productDiag.snapshotsAvailable}
            />
          )}
          <OrdersTable
            orders={orders}
            allOrders={allOrders}
            onSelectOrder={setSelectedOrder}
          />
        </StaleResults>
      )}

      {/* ── Live Commerce data section ────────────────────────── */}
      {productMode === 'live' && selectionMode === 'company' && liveStats && (
        <StaleResults stale={staleResult} processLabel="Processar Live">
          <ProductSectionLabel product="live" />
          <LiveStatsCard
            stats={liveStats}
            selectedCompany={selectedCompany}
            startDate={resultStart}
            endDate={resultEnd}
          />
        </StaleResults>
      )}

      {/* ── Segment / Group mode (always Video Commerce for now) ─ */}
      {selectionMode === 'group' && segmentResult && (
        <StaleResults stale={staleResult} processLabel="Processar Stories">
          <ProductSectionLabel product="stories" />
          <SegmentStatsCard
            result={segmentResult}
            startDate={resultStart}
            endDate={resultEnd}
            datesChanged={datesChanged}
            showToast={showToast}
          />
        </StaleResults>
      )}
    </>
  )
}
